/**
 * Query scope for the Financing and approval tab (BAR-2829): Patients the stage
 * bot tagged `financing-approval`, narrowed by the Entered preset as `stage-day`
 * tags (BAR-2951) and the hub's filters on their tags. Ready to schedule is left
 * to the To schedule tab (BAR-3095).
 */

import {
  BariatricProcedureStage,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
  PATIENT_FILTER_TAG_SYSTEMS,
  PAYMENT_STATUS_TAG_SYSTEMS,
  Track,
} from '@bariendo/lib/constants';
import { financingStepOptions } from '@bariendo/lib/payment-status';
import { Meta } from '@medplum/fhirtypes';
import { dayTagClause, ListRange } from '@/components/Hub/listRange';
import { patientFilterClauses, QUEUE_FILTER_ANY, QueueFilterValues } from './queueFilters';

export const FINANCING_STAGE_TAG = `${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${BariatricProcedureStage.FINANCING_APPROVAL}`;

/**
 * The Step filter's options (BAR-3095): every Financing & approval status on any track but
 * Ready to schedule, whose patients are on To schedule.
 */
export const FINANCING_STEP_OPTIONS = financingStepOptions([Track.D2C, Track.EMPLOYER, Track.INSURANCE], {
  withoutReadyToSchedule: true,
});

const FINANCING_STEP_CODES = new Set<string>(FINANCING_STEP_OPTIONS.map((option) => option.value));

export function buildFinancingFilter(range: ListRange, values: QueueFilterValues): string {
  const entered = dayTagClause(PATIENT_FILTER_TAG_SYSTEMS.stageDay, range);
  // An include list, not a negation: a `ne` on the multi-valued `_tag` isn't proven on Medplum.
  const anyStep = `(${[...FINANCING_STEP_CODES]
    .map((step) => `_tag eq "${PAYMENT_STATUS_TAG_SYSTEMS.status}|${step}"`)
    .join(' or ')})`;
  const parts = [
    `_tag eq "${FINANCING_STAGE_TAG}"`,
    ...(entered ? [entered] : []),
    ...(values.step === QUEUE_FILTER_ANY ? [anyStep] : []),
    ...patientFilterClauses(values, ''),
  ];

  return `(${parts.join(' and ')})`;
}

/** For rows that arrive by subscription: one now Ready to schedule, or untagged, leaves the tab. */
export function matchesFinancingStep(patient: { meta?: Meta } | undefined): boolean {
  return (patient?.meta?.tag ?? []).some(
    (tag) => tag.system === PAYMENT_STATUS_TAG_SYSTEMS.status && FINANCING_STEP_CODES.has(tag.code ?? '')
  );
}

/** A plain parameter: a `_filter` criteria never fires (see `useLiveGraphQL`). */
export function financingSubscriptionParams(): Record<string, string> {
  return { _tag: FINANCING_STAGE_TAG };
}
